import { buildTree } from "@/engine/cpm";
import type { Dependency, InsertStart, Project, Schedule, Task, TaskConstraint } from "@/engine/types";

/** Tasks are stored in document order (depth-first). These helpers keep that invariant. */

export function newId(prefix = "t"): string {
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/** Return tasks in canonical document order (children right after their parent). */
export function normalizeOrder(tasks: Task[]): Task[] {
  const tree = buildTree(tasks);
  return tree.order.map((id) => tree.byId.get(id)!);
}

export function subtreeIds(tasks: Task[], rootId: string): string[] {
  const tree = buildTree(tasks);
  const out: string[] = [];
  for (const id of tree.order) {
    if (id === rootId || tree.ancestors.get(id)?.includes(rootId)) out.push(id);
  }
  return out;
}

function extractBlock(tasks: Task[], rootId: string): { rest: Task[]; block: Task[] } {
  const ids = new Set(subtreeIds(tasks, rootId));
  return { rest: tasks.filter((t) => !ids.has(t.id)), block: tasks.filter((t) => ids.has(t.id)) };
}

function insertAfterSubtree(tasks: Task[], anchorId: string | null, block: Task[]): Task[] {
  if (!anchorId) return [...tasks, ...block];
  const ids = new Set(subtreeIds(tasks, anchorId));
  let lastIdx = -1;
  tasks.forEach((t, i) => {
    if (ids.has(t.id)) lastIdx = i;
  });
  return [...tasks.slice(0, lastIdx + 1), ...block, ...tasks.slice(lastIdx + 1)];
}

export function makeTask(partial: Partial<Task> = {}): Task {
  return {
    id: newId(),
    parentId: null,
    name: "",
    duration: 1,
    percentComplete: 0,
    progressMethod: "percent",
    ...partial,
  };
}

/** Insert a sibling right after `afterId`'s subtree (or at the end when null). */
export function addSibling(tasks: Task[], afterId: string | null, task: Task): Task[] {
  const anchor = afterId ? tasks.find((t) => t.id === afterId) : undefined;
  const t: Task = { ...task, parentId: anchor?.parentId ?? null };
  return insertAfterSubtree(tasks, afterId, [t]);
}

/** Append a child at the end of `parentId`'s children. */
export function addChild(tasks: Task[], parentId: string, task: Task): Task[] {
  const t: Task = { ...task, parentId };
  return insertAfterSubtree(tasks, parentId, [t]);
}

/**
 * Start for a task inserted next to `anchorId`: the anchor's scheduled start as an auto SNET.
 * None without an anchor, or when the anchor already starts on the project start (ASAP lands there anyway).
 */
export function insertConstraint(schedule: Schedule, anchorId: string | null): TaskConstraint | undefined {
  const sched = anchorId ? schedule.tasks.get(anchorId) : undefined;
  if (!sched || sched.start <= schedule.projectStart) return undefined;
  return { type: "SNET", date: sched.start, auto: true };
}

export const DEFAULT_NEW_TASK_DURATION = 3;

export type InsertTarget = { kind: "sibling"; afterId: string | null } | { kind: "child"; parentId: string };

/**
 * Build a new task (and the FS link that places it, in "link" mode) from the project's new-task settings.
 * Precedence: project defaults < what a leaf parent hands down < placement < `partial` from the caller.
 *
 * A leaf parent is about to become a summary, whose own duration, progress and constraint the engine
 * ignores. Its constraint always moves to the first child so the parent stays where it was; duration,
 * progress and assignee move too when `childInheritsParent` is on.
 */
export function planInsert(
  project: Project,
  schedule: Schedule,
  target: InsertTarget,
  partial: Partial<Task> = {},
): { task: Task; link: Omit<Dependency, "id"> | null } {
  const mode = resolveInsertStart(project.insertStart);
  const defaults: Partial<Task> = {
    duration: normalizeDefaultDuration(project.defaultDuration),
    progressMethod: project.defaultProgressMethod ?? "percent",
  };
  let handedDown: Partial<Task> = {};
  let constraint: TaskConstraint | undefined;
  let predecessorId: string | null = null;

  if (target.kind === "child") {
    const parent = project.tasks.find((t) => t.id === target.parentId);
    const children = project.tasks.filter((t) => t.parentId === target.parentId);
    if (parent && children.length === 0) {
      if (parent.constraint) constraint = { ...parent.constraint };
      else if (mode === "anchor") constraint = insertConstraint(schedule, parent.id);
      if (project.childInheritsParent ?? true) handedDown = inheritedFrom(parent);
    } else if (mode === "anchor") {
      constraint = insertConstraint(schedule, target.parentId);
    } else if (mode === "link") {
      predecessorId = children.at(-1)?.id ?? null;
    }
  } else if (target.afterId && project.tasks.some((t) => t.id === target.afterId)) {
    if (mode === "anchor") constraint = insertConstraint(schedule, target.afterId);
    else if (mode === "link") predecessorId = target.afterId;
  }

  const task = makeTask({ ...defaults, ...handedDown, ...(constraint ? { constraint } : {}), ...partial });
  const link = predecessorId ? { predecessorId, successorId: task.id, type: "FS" as const, lag: 0 } : null;
  return { task, link };
}

export function resolveInsertStart(value: unknown): InsertStart {
  return value === "link" || value === "project" ? value : "anchor";
}

export function normalizeDefaultDuration(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) return DEFAULT_NEW_TASK_DURATION;
  return Math.min(999, Math.max(1, Math.round(value)));
}

function inheritedFrom(parent: Task): Partial<Task> {
  const out: Partial<Task> = { percentComplete: parent.percentComplete, progressMethod: parent.progressMethod };
  if (parent.duration > 0) out.duration = parent.duration;
  if (parent.assignee) out.assignee = parent.assignee;
  if (parent.assignedResourceId) out.assignedResourceId = parent.assignedResourceId;
  return out;
}

/**
 * Drop auto constraints from tasks that now have a predecessor, directly or through an ancestor summary
 * (the engine expands a summary successor to its leaves). Returns the same project when nothing changes.
 */
export function releaseAutoConstraints(project: Project): Project {
  if (!project.tasks.some((t) => t.constraint?.auto)) return project;
  const ids = new Set(project.tasks.map((t) => t.id));
  const successors = new Set(project.dependencies.filter((d) => ids.has(d.predecessorId) && d.predecessorId !== d.successorId).map((d) => d.successorId));
  if (!successors.size) return project;
  const parentOf = new Map(project.tasks.map((t) => [t.id, t.parentId] as const));
  const linked = (id: string) => {
    const seen = new Set<string>();
    for (let cur: string | null | undefined = id; cur && !seen.has(cur); cur = parentOf.get(cur)) {
      if (successors.has(cur)) return true;
      seen.add(cur);
    }
    return false;
  };
  let changed = false;
  const tasks = project.tasks.map((t) => {
    if (!t.constraint?.auto || !linked(t.id)) return t;
    changed = true;
    const { constraint: _released, ...rest } = t;
    return rest;
  });
  return changed ? { ...project, tasks } : project;
}

export function removeTasks(project: Project, ids: string[]): Pick<Project, "tasks" | "dependencies"> {
  const all = new Set<string>();
  for (const id of ids) for (const s of subtreeIds(project.tasks, id)) all.add(s);
  return {
    tasks: project.tasks.filter((t) => !all.has(t.id)),
    dependencies: project.dependencies.filter((d) => !all.has(d.predecessorId) && !all.has(d.successorId)),
  };
}

export function siblingsOf(tasks: Task[], id: string): { siblings: Task[]; index: number; parentId: string | null } {
  const me = tasks.find((t) => t.id === id)!;
  const siblings = tasks.filter((t) => t.parentId === me.parentId);
  return { siblings, index: siblings.findIndex((t) => t.id === id), parentId: me.parentId };
}

/** Make the task a child of its previous sibling. */
export function indent(tasks: Task[], id: string): Task[] {
  const { siblings, index } = siblingsOf(tasks, id);
  if (index <= 0) return tasks;
  const prev = siblings[index - 1]!;
  const { rest, block } = extractBlock(tasks, id);
  const moved = block.map((t) => (t.id === id ? { ...t, parentId: prev.id } : t));
  return insertAfterSubtree(rest, prev.id, moved);
}

/** Move the task up one level, placing it after its former parent's subtree. */
export function outdent(tasks: Task[], id: string): Task[] {
  const me = tasks.find((t) => t.id === id)!;
  if (!me.parentId) return tasks;
  const parent = tasks.find((t) => t.id === me.parentId)!;
  const { rest, block } = extractBlock(tasks, id);
  const moved = block.map((t) => (t.id === id ? { ...t, parentId: parent.parentId } : t));
  return insertAfterSubtree(rest, parent.id, moved);
}

export function moveUp(tasks: Task[], id: string): Task[] {
  const { siblings, index } = siblingsOf(tasks, id);
  if (index <= 0) return tasks;
  const prev = siblings[index - 1]!;
  const { rest, block } = extractBlock(tasks, id);
  const prevIdx = rest.findIndex((t) => t.id === prev.id);
  return [...rest.slice(0, prevIdx), ...block, ...rest.slice(prevIdx)];
}

export function moveDown(tasks: Task[], id: string): Task[] {
  const { siblings, index } = siblingsOf(tasks, id);
  if (index < 0 || index >= siblings.length - 1) return tasks;
  const next = siblings[index + 1]!;
  const { rest, block } = extractBlock(tasks, id);
  return insertAfterSubtree(rest, next.id, block);
}

/**
 * Wrap the selected tasks in a new summary task. Only the top-most selected tasks ("roots") are moved;
 * they must share one parent. Returns null when the selection cannot be grouped.
 */
export function groupTasks(tasks: Task[], ids: string[], summary: Task): Task[] | null {
  const tree = buildTree(tasks);
  const sel = new Set(ids);
  const roots = tree.order.filter((id) => sel.has(id) && !(tree.ancestors.get(id) ?? []).some((a) => sel.has(a)));
  if (!roots.length) return null;
  const parentId = tree.byId.get(roots[0]!)!.parentId;
  if (roots.some((id) => tree.byId.get(id)!.parentId !== parentId)) return null;
  const group: Task = { ...summary, parentId };
  // insert the summary right before the first root
  const firstIdx = tasks.findIndex((t) => t.id === roots[0]);
  let out = [...tasks.slice(0, firstIdx), group, ...tasks.slice(firstIdx)];
  for (const id of roots) {
    const { rest, block } = extractBlock(out, id);
    out = insertAfterSubtree(rest, group.id, block.map((t) => (t.id === id ? { ...t, parentId: group.id } : t)));
  }
  return out;
}

export function updateTask(tasks: Task[], id: string, patch: Partial<Task>): Task[] {
  return tasks.map((t) => (t.id === id ? { ...t, ...patch } : t));
}

/** Row number (1-based, document order) ↔ id helpers used by the "선행" column. */
export function rowNumberMap(tasks: Task[]): { byId: Map<string, number>; byRow: Map<number, string> } {
  const byId = new Map<string, number>();
  const byRow = new Map<number, string>();
  tasks.forEach((t, i) => {
    byId.set(t.id, i + 1);
    byRow.set(i + 1, t.id);
  });
  return { byId, byRow };
}

export function formatPredecessors(project: Project, taskId: string): string {
  const { byId } = rowNumberMap(project.tasks);
  return project.dependencies
    .filter((d) => d.successorId === taskId)
    .map((d) => {
      const n = byId.get(d.predecessorId);
      if (!n) return "";
      const type = d.type === "FS" ? "" : d.type;
      const lag = d.lag ? (d.lag > 0 ? `+${d.lag}` : `${d.lag}`) : "";
      return `${n}${type}${lag}`;
    })
    .filter(Boolean)
    .join(", ");
}

/** Parse "3, 5SS+2, 7FF-1" into dependencies for `taskId`. Returns null when unparsable. */
export function parsePredecessors(project: Project, taskId: string, text: string): Omit<Dependency, "id">[] | null {
  const { byRow } = rowNumberMap(project.tasks);
  const out: Omit<Dependency, "id">[] = [];
  const parts = text
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  for (const p of parts) {
    const m = /^(\d+)(FS|SS|FF|SF)?([+-]\d+)?$/i.exec(p);
    if (!m) return null;
    const predId = byRow.get(Number(m[1]));
    if (!predId || predId === taskId) return null;
    out.push({
      predecessorId: predId,
      successorId: taskId,
      type: (m[2]?.toUpperCase() as Dependency["type"]) ?? "FS",
      lag: m[3] ? Number(m[3]) : 0,
    });
  }
  return out;
}
